import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

type H = { id: string; ref: string; text: string; version: string; source_title: string; link: string; created_at: string; note?: string };
const store: Record<string, { items: H[] | null; subs: Set<(x: H[]) => void>; loading?: Promise<void> }> = {};

function useBase() {
  const { kid, user } = useAuth();
  return `${user?.id || "anon"}|${kid ? `/kids/${kid.id}/highlights` : "/highlights"}`;
}
const path = (base: string) => base.split("|")[1];

function slot(base: string) {
  return (store[base] ||= { items: null, subs: new Set() });
}

function publish(base: string, items: H[]) {
  const s = slot(base);
  s.items = items;
  s.subs.forEach((f) => f(items));
}

export function useHighlights() {
  const base = useBase();
  const [items, setItems] = useState<H[] | null>(slot(base).items);
  useEffect(() => {
    const s = slot(base);
    s.subs.add(setItems);
    if (!s.items && !s.loading) s.loading = api.get(path(base)).then((r) => publish(base, r.data)).catch(() => publish(base, [])).finally(() => { s.loading = undefined; });
    else if (s.items) setItems(s.items);
    return () => { s.subs.delete(setItems); };
  }, [base]);

  const find = (ref: string, text: string) => items?.find((h) => h.ref === ref && h.text === text);
  const save = async (ref: string, text: string, source_title: string, link: string) => {
    try { const { data } = await api.post(path(base), { ref, text, source_title, link }); publish(base, [data, ...(slot(base).items || []).filter((h) => h.id !== data.id)]); toast.success("Verse saved to your collection"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id: string) => {
    try { await api.delete(`${path(base)}/${id}`); publish(base, (slot(base).items || []).filter((h) => h.id !== id)); toast.success("Verse removed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setNote = async (id: string, note: string) => {
    try { const { data } = await api.put(`${path(base)}/${id}/note`, { note }); publish(base, (slot(base).items || []).map((h) => (h.id === id ? { ...h, note: data.note } : h))); toast.success("Note saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return { items, find, save, remove, setNote };
}
